import { useState, useEffect } from 'react';
import { Input, Collapse, Space, Button, Spin } from 'antd';
import { SearchOutlined } from '@ant-design/icons';
import { getWorkflowNodes } from '../../API/services/workflowsService';
import { t } from '../../i18n/i18n';
import NodeCard from './NodeCard';

const { Panel } = Collapse;
const { Search } = Input;

const NodePanel = ({ sandbox, hideFooter = false }) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [activeCategories, setActiveCategories] = useState(['Triggers']);
  const [nodeTemplates, setNodeTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const loadNodeTemplates = async () => {
      try {
        setLoading(true);
        const templates = await getWorkflowNodes();
        setNodeTemplates(templates);
        setError(null);
      } catch (err) {
        setError(t('ALKYMATION_ERROR_LOADING_NODES'));
      } finally {
        setLoading(false);
      }
    };

    loadNodeTemplates();
  }, []);

  const nodeCategories = [...new Set(nodeTemplates.map((template) => template.category))];

  const getNodesByCategory = (category) =>
    nodeTemplates.filter((template) => template.category === category);

  const filteredTemplates = nodeTemplates.filter(
    (template) =>
      t(template.name).toLowerCase().includes(searchTerm.toLowerCase()) ||
      t(template.description).toLowerCase().includes(searchTerm.toLowerCase())
  );

  const handleAddNodeToCanvas = (template) => {
    const centerPosition = {
      x: 200 + Math.random() * 200,
      y: 150 + Math.random() * 200,
    };
    sandbox.addNode(template, centerPosition);
  };
  return (
    <div className="node-panel">
      <div className="panel-header">
        <h4>{t('ALKYMATION_AVAILABLE_NODES')}</h4>
        <Search
          placeholder={t('ALKYMATION_SEARCH_NODES')}
          prefix={<SearchOutlined />}
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          size="small"
          allowClear
        />
      </div>

      <div className="panel-content">
        {loading ? (
          <div className="d-flex justify-content-center p-4">
            <Spin size="large" />
          </div>
        ) : error ? (
          <div className="text-center p-4 text-danger">
            <p>{error}</p>
            <Button onClick={() => window.location.reload()}>{t('ALKYMATION_RETRY')}</Button>
          </div>
        ) : (
          <Collapse activeKey={activeCategories} onChange={setActiveCategories} ghost>
            {nodeCategories.map((category) => {
              const categoryNodes = getNodesByCategory(category).filter((template) =>
                filteredTemplates.includes(template)
              );

              if (categoryNodes.length === 0) return null;

              return (
                <Panel
                  header={
                    <div className="category-header">
                      <span>{t(category)}</span>
                      <span className="category-count">({categoryNodes.length})</span>
                    </div>
                  }
                  key={category}
                >
                  <Space direction="vertical" size="small" className="w-100">
                    {categoryNodes.map((template) => (
                      <NodeCard
                        key={template.templateId}
                        template={template}
                        onAddToCanvas={handleAddNodeToCanvas}
                      />
                    ))}
                  </Space>
                </Panel>
              );
            })}
          </Collapse>
        )}

        {!loading && filteredTemplates.length === 0 && searchTerm && (
          <div className="no-results">
            <p>
              {t('ALKYMATION_NO_RESULTS_FOUND')} &quot;{searchTerm}&quot;
            </p>
          </div>
        )}
      </div>

      {!hideFooter && (
        <div className="panel-footer">
          <div className="workflow-info">
            <small>
              <strong>{t('ALKYMATION_CURRENT_WORKFLOW')}:</strong>
              <br />
              {sandbox.workflow?.name || t('ALKYMATION_WITHOUT_NAME')}
              <br />
              <span className="text-muted">
                {sandbox.workflow?.nodes?.length || 0} {t('ALKYMATION_NODE_COUNT')},{' '}
                {sandbox.workflow?.connections?.length || 0} {t('ALKYMATION_CONNECTION_COUNT')}
              </span>
            </small>
          </div>
        </div>
      )}
    </div>
  );
};

export default NodePanel;
